// src/components/sourcex/CopyQuestionModal.jsx
//
// "Copy question" in the source-document editor (2026-10-29): copy ONE question
// into a heading of this visit (the same heading or another one) or of another
// visit in the same part of the template, the same visits "Copy section" offers.
// Where it lands comes from questionCopyTargets (templateSchema.js); the copy
// itself is the editor's, through saveTemplate, so it is undoable and audited.
import { useMemo, useState } from "react";
import { Copy } from "lucide-react";
import { questionCopyTargets } from "../../services/templateSchema";

/**
 * @param {object}   props
 * @param {object}   props.question       the question being copied
 * @param {number}   props.sourceIndex    its position in the current visit
 * @param {string}   props.currentVisit   the current visit's name
 * @param {{eventName: string, questions: object[], status?: string}[]} props.visits
 *   the visits in this part of the template (current one included)
 * @param {(target: {visitName: string, targetKey: string}) => void} props.onCopy
 * @param {() => void} props.onCancel
 */
export default function CopyQuestionModal({ question, sourceIndex, currentVisit, visits, onCopy, onCancel }) {
  const ordered = useMemo(() => {
    const list = (visits || []).filter((v) => v && v.eventName);
    const here = list.filter((v) => v.eventName === currentVisit);
    return [...here, ...list.filter((v) => v.eventName !== currentVisit)];
  }, [visits, currentVisit]);

  const [visitName, setVisitName] = useState(currentVisit);
  const visit = ordered.find((v) => v.eventName === visitName) || ordered[0];
  const sameVisit = visit?.eventName === currentVisit;
  const targets = useMemo(
    () => questionCopyTargets(visit?.questions || [], sameVisit ? { sourceIndex } : {}),
    [visit, sameVisit, sourceIndex]
  );
  const [picked, setPicked] = useState(null);
  // The default place: the original's own heading in this visit, else the first.
  const targetKey =
    picked && targets.some((t) => t.key === picked)
      ? picked
      : (targets.find((t) => t.holdsOriginal) || targets[0])?.key;

  const label = question?.question || "this question";
  const ruleDropped = !sameVisit && !!question?.visibleWhen;

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Copy question"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") onCancel();
      }}
    >
      <div className="copy-question-modal w-full max-w-md rounded-2xl border border-white/12 bg-[#0c0826] shadow-[0_24px_70px_-20px_rgba(0,0,0,0.9)] overflow-hidden">
        <div className="px-5 pt-4">
          <h3 className="text-sm font-semibold text-white">Copy “{label}”</h3>
          <p className="mt-1 text-[12px] text-slate-400">
            Pick a visit, then the heading to put the copy under. It goes at the end of that heading, or right below
            the original in its own heading.
          </p>
        </div>

        <div className="mt-3 px-5">
          <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-400" htmlFor="copy-q-visit">
            Visit
          </label>
          <select
            id="copy-q-visit"
            value={visit?.eventName || ""}
            onChange={(e) => {
              setVisitName(e.target.value);
              setPicked(null);
            }}
            className="copy-question-select mt-1 w-full rounded-lg border border-white/10 bg-white/[0.04] px-2 py-1.5 text-[12px] text-slate-100"
          >
            {ordered.map((v) => (
              <option key={v.eventName} value={v.eventName}>
                {v.eventName}
                {v.eventName === currentVisit ? " (this visit)" : ""}
              </option>
            ))}
          </select>
        </div>

        <fieldset className="mt-3 max-h-[40vh] overflow-auto px-5">
          <legend className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Under heading</legend>
          <ul className="mt-1 space-y-1 pb-1">
            {targets.map((t) => (
              <li key={t.key}>
                <label className="flex items-start gap-2 rounded-lg px-2 py-1.5 cursor-pointer hover:bg-white/[0.05]">
                  <input
                    type="radio"
                    name="copy-q-target"
                    checked={targetKey === t.key}
                    onChange={() => setPicked(t.key)}
                    className="mt-0.5 accent-sky-400"
                  />
                  <span className="min-w-0 text-[12px] text-slate-200">
                    {t.label}
                    {t.holdsOriginal && <span className="text-slate-400"> (right below the original)</span>}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>

        {(ruleDropped || visit?.status === "approved") && (
          <div className="copy-question-note mx-5 mt-2 space-y-1 rounded-lg border border-amber-400/30 bg-amber-400/10 p-2.5 text-[11px] text-amber-100">
            {ruleDropped && (
              <p>
                This question has a show/hide rule pointing at a question in this visit. That question isn't in{" "}
                {visit.eventName}, so the copy's rule is removed and it is always shown.
              </p>
            )}
            {visit?.status === "approved" && (
              <p>{visit.eventName} is approved. After this copy it will need to be approved again.</p>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 px-5 py-3 mt-3 border-t border-white/10 bg-white/[0.02]">
          <button
            onClick={onCancel}
            className="px-3 py-2 rounded-xl text-sm text-slate-300 bg-white/[0.05] border border-white/10 hover:bg-white/[0.12] transition"
          >
            Cancel
          </button>
          <button
            onClick={() => onCopy({ visitName: visit.eventName, targetKey })}
            disabled={!visit || !targetKey}
            className="copy-question-confirm inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-sky-500 to-blue-500 hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            <Copy className="w-3.5 h-3.5" />
            Copy question
          </button>
        </div>
      </div>
    </div>
  );
}
